import { useState, useCallback, useRef } from 'react';
import { Upload, CheckCircle2, AlertCircle, File, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { writeFilesToPracticeFolder, requestPermissionForHandle, hasSavedHandle } from '@/hooks/useFileSystem';

interface ActivityFileDropzoneProps {
  open: boolean;
  onClose: () => void;
  activityTitle: string;
  practiceId?: string;
  practiceDocumentPath?: string;
  subfolder?: string;
  activityId?: string;
  onUploadSuccess?: () => void;
}

export default function ActivityFileDropzone({
  open,
  onClose,
  activityTitle,
  practiceId,
  practiceDocumentPath,
  subfolder,
  activityId,
  onUploadSuccess,
}: ActivityFileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [copiedFiles, setCopiedFiles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError(null);

    const items = Array.from(e.dataTransfer.files);
    if (items.length === 0) return;

    if (!practiceId) {
      setError('Pratica non disponibile.');
      return;
    }

    try {
      const written = await writeFilesToPracticeFolder(practiceId, items, subfolder, practiceDocumentPath);
      if (written === null) {
        const saved = await hasSavedHandle(practiceId);
        if (saved) {
          setNeedsAuth(true);
          setPendingFiles(items);
          setError('Permesso di accesso alla cartella scaduto. Clicca "Autorizza accesso" per continuare.');
        } else {
          setError('Cartella documenti non configurata. Configura prima la cartella nella pratica.');
        }
        return;
      }
      setCopiedFiles(prev => [...prev, ...written]);
      toast.success(`${written.length} file copiati nella cartella`);
    } catch (err: any) {
      console.error('File copy error:', err);
      const isPermError = err?.name === 'NotAllowedError' || err?.name === 'SecurityError';
      if (isPermError && practiceId) {
        setNeedsAuth(true);
        setPendingFiles(items);
        setError('Permesso di accesso scaduto. Clicca "Autorizza accesso" per continuare.');
      } else {
        setError(`Errore durante la copia: ${err?.message || 'errore sconosciuto'}`);
        toast.error(`Errore copia file: ${err?.message || 'errore sconosciuto'}`);
      }
    }
  }, [practiceId, subfolder, practiceDocumentPath]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !practiceId) return;
    setError(null);
    setNeedsAuth(false);
    const items = Array.from(files);
    try {
      const written = await writeFilesToPracticeFolder(practiceId, items, subfolder, practiceDocumentPath);
      if (written === null) {
        const saved = await hasSavedHandle(practiceId);
        if (saved) {
          setNeedsAuth(true);
          setPendingFiles(items);
          setError('Permesso di accesso alla cartella scaduto. Clicca "Autorizza accesso" per continuare.');
        } else {
          setError('Cartella documenti non configurata. Configura prima la cartella nella pratica.');
        }
        return;
      }
      setCopiedFiles(prev => [...prev, ...written]);
      toast.success(`${written.length} file copiati nella cartella`);
    } catch (err: any) {
      console.error('File copy error:', err);
      const isPermError = err?.name === 'NotAllowedError' || err?.name === 'SecurityError';
      if (isPermError && practiceId) {
        setNeedsAuth(true);
        setPendingFiles(items);
        setError('Permesso di accesso scaduto. Clicca "Autorizza accesso" per continuare.');
      } else {
        setError(`Errore durante la copia: ${err?.message || 'errore sconosciuto'}`);
        toast.error(`Errore copia file: ${err?.message || 'errore sconosciuto'}`);
      }
    }
    e.target.value = '';
  }, [practiceId, subfolder, practiceDocumentPath]);

  const handleAuthorizeAndRetry = useCallback(async () => {
    if (!practiceId) return;
    const granted = await requestPermissionForHandle(practiceId);
    if (!granted) {
      toast.error('Permesso negato. Seleziona la cartella dalla pratica.');
      return;
    }
    setError(null);
    setNeedsAuth(false);
    if (pendingFiles.length > 0) {
      try {
        const written = await writeFilesToPracticeFolder(practiceId, pendingFiles, subfolder, practiceDocumentPath);
        if (written === null) {
          setError('Errore: impossibile scrivere nella cartella.');
          return;
        }
        setCopiedFiles(prev => [...prev, ...written]);
        setPendingFiles([]);
        toast.success(`${written.length} file copiati nella cartella`);
      } catch {
        setError('Errore durante la copia dei file');
        toast.error('Errore durante la copia dei file');
      }
    } else {
      toast.success('Accesso autorizzato. Puoi ora trascinare i file.');
    }
  }, [practiceId, pendingFiles, subfolder]);

  const handleClose = useCallback(() => {
    onClose();
    setCopiedFiles([]);
    setError(null);
    setNeedsAuth(false);
    setPendingFiles([]);
  }, [onClose]);

  const handleSaveAndClose = useCallback(() => {
    if (copiedFiles.length > 0 && onUploadSuccess) {
      onUploadSuccess();
    }
    handleClose();
  }, [copiedFiles.length, onUploadSuccess, handleClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Inserisci File - {activityTitle}</DialogTitle>
        </DialogHeader>

        {practiceDocumentPath && (
          <p className="text-sm text-gray-500">
            Cartella pratica: <span className="font-mono text-xs">{practiceDocumentPath}</span>
            {subfolder && <span> / <span className="font-mono text-xs">{subfolder}</span></span>}
          </p>
        )}

        {!practiceId ? (
          <p className="text-sm text-amber-600">Pratica non disponibile.</p>
        ) : (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
                isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              )}
            >
              <Upload className={cn('h-10 w-10 mx-auto mb-3', isDragging ? 'text-blue-500' : 'text-gray-400')} />
              <p className="text-sm font-medium text-gray-700">
                {isDragging ? 'Rilascia qui i file' : 'Trascina i file qui'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                I file verranno copiati nella cartella dell'attività
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileInput}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => fileInputRef.current?.click()}
              >
                Seleziona file
              </Button>
            </div>

            {error && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
                <div className="flex gap-2">
                  {needsAuth && practiceId && (
                    <Button variant="default" size="sm" onClick={handleAuthorizeAndRetry}>
                      <FolderOpen className="h-4 w-4 mr-1" />
                      Autorizza Accesso
                    </Button>
                  )}
                  {practiceId && !needsAuth && (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/practices/${practiceId}?tab=documents`}>
                        Configura cartella nella pratica
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            )}

            {copiedFiles.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">File copiati:</p>
                {copiedFiles.map((name, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle2 className="h-3 w-3" />
                    <File className="h-3 w-3" />
                    {name}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Chiudi
          </Button>
          <Button onClick={handleSaveAndClose} disabled={copiedFiles.length === 0}>
            Salva
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
